var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from 'react';
import Card from '../card';
import Button from '../button';
import StatusStepper from './status-stepper';
import PaymentModal from './payment-modal';
import PaymentWaitingModal from './payment-waiting-modal';
import RegistrationModal from './registration-modal';
import { CRM_MONTHLY_FEE } from './types';
import { useCRMStatus, refreshCRMStatus, subscribeCRM } from '../../../http/crm';
import { navigate } from '../../../utility/general';
const FEATURES = [
    {
        'title': 'Two-Way SMS',
        'icon': '💬',
        'description': 'Send and receive texts with your customers right in Rate Remover. Each shop gets its own dedicated number registered with the carriers.',
        'hash': '#/crm/conversations',
        'docHref': '/things-to-know-about-two-way-sms.html',
    },
    {
        'title': 'Email Campaigns',
        'icon': '✉️',
        'description': 'Run service reminders, follow-ups, and seasonal campaigns. Templates, sequences, and tracking built in.',
        'hash': '#/crm/email-campaigns',
    },
    {
        'title': 'Online Scheduling',
        'icon': '📅',
        'description': 'Customers book appointments from a public link. Times sync to your shop calendar automatically.',
        'hash': '#/crm/scheduling',
    },
];
export default (props) => {
    const status = useCRMStatus(props.merchantId);
    const [showPayment, setShowPayment] = React.useState(false);
    const [showWaiting, setShowWaiting] = React.useState(false);
    const [showRegistration, setShowRegistration] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const handleGetStarted = () => setShowPayment(true);
    const handleResumeRegistration = () => setShowRegistration(true);
    const handleManageCRM = () => {
        // Placeholder — wires up to the CRM dashboard once that screen lands.
        alert('CRM management screens are coming soon.');
    };
    const handlePaymentConfirm = () => __awaiter(void 0, void 0, void 0, function* () {
        setLoading(true);
        try {
            const { redirect_url } = yield subscribeCRM(props.merchantId);
            window.open(redirect_url, '_blank');
            setShowPayment(false);
            setShowWaiting(true);
        }
        catch (e) {
            alert('Could not start the payment. Please try again in a moment.');
            console.error('subscribeCRM failed:', e);
        }
        finally {
            setLoading(false);
        }
    });
    const handlePaymentActive = () => {
        setShowWaiting(false);
        refreshCRMStatus();
        setShowRegistration(true);
    };
    const handleRegistrationSubmit = (_form) => {
        // Stub: real wiring POSTs to /merchants/{id}/crm/registration which
        // submits to GHL and advances the workspace state machine. For now,
        // just close the modal — the poll or refresh picks up the next state.
        setLoading(true);
        setTimeout(() => {
            setShowRegistration(false);
            refreshCRMStatus();
            setLoading(false);
        }, 400);
    };
    const renderCTA = () => {
        switch (status.state) {
            case 'not_started':
                return _jsxs("div", { className: "d-flex flex-column align-items-start gap-2", children: [_jsx("p", { className: "mb-0", children: "Ready to add a CRM to your shop?" }), _jsx(Button, { color: "success", onClick: handleGetStarted, children: "Get Started" })] });
            case 'info_requested':
                return _jsxs("div", { children: [_jsx("p", { className: "mb-2", children: "Payment received. Tell us a bit about your business and we will provision your CRM." }), _jsx(Button, { color: "primary", onClick: handleResumeRegistration, children: "Complete Setup" })] });
            case 'sub_account_pending':
            case 'phone_provisioning':
            case 'a2p_pending':
                return _jsxs("div", { children: [_jsx("p", { className: "mb-3", children: "Your CRM is being set up. You will receive an email when each step is complete." }), _jsx(StatusStepper, { state: status.state })] });
            case 'active':
                return _jsxs("div", { children: [_jsxs("div", { className: "alert alert-success mb-3", children: ["Your CRM is live", status.phone_number ? _jsxs(_Fragment, { children: [" at ", _jsx("strong", { children: status.phone_number })] }) : null, "."] }), _jsx(Button, { color: "primary", onClick: handleManageCRM, children: "Manage CRM" })] });
            case 'a2p_rejected':
                return _jsxs("div", { children: [_jsxs("div", { className: "alert alert-danger mb-3", children: [_jsx("strong", { children: "Carrier approval failed." }), !!status.rejection_reason && _jsx("div", { className: "small mt-1", children: status.rejection_reason })] }), _jsx(Button, { color: "primary", onClick: handleResumeRegistration, children: "Update Info & Retry" })] });
            case 'cancelled':
                return _jsx("div", { children: _jsx("div", { className: "alert alert-secondary mb-0", children: "Your CRM is currently inactive. Please contact support to restore access." }) });
        }
    };
    const isActive = status.state === 'active';
    return _jsxs("div", { className: "d-flex flex-column gap-3", children: [_jsx(Card, { title: null, children: _jsxs("div", { className: "p-2", children: [_jsx("h3", { className: "mb-2", children: isActive ? 'Your CRM' : 'Add a built-in CRM to your shop' }), _jsx("p", { className: "mb-0 text-muted", children: isActive
                                ? 'Open any of the tools below to manage conversations, campaigns, and online booking.'
                                : 'Talk to customers, run campaigns, and let them book online, all from inside Rate Remover. Works with any subscription plan.' })] }) }), _jsx("div", { className: "row g-3", children: FEATURES.map((f) => _jsx("div", { className: "col-md-4", children: _jsx(Card, { title: null, className: "h-100", children: _jsxs("div", { className: "p-2 text-center h-100 d-flex flex-column", children: [_jsx("div", { style: { 'fontSize': '2.5rem' }, className: "mb-2", children: f.icon }), _jsx("h5", { className: "mb-2", children: f.title }), _jsx("p", { className: "text-muted small mb-3 flex-grow-1", children: f.description }), status.state === 'active' && (_jsx(Button, { color: "primary", onClick: () => navigate(f.hash), children: "Open" })), !!f.docHref && (_jsx("div", { className: "mt-2", children: _jsx("a", { href: f.docHref, target: "_blank", rel: "noopener noreferrer", className: "small", children: "Important SMS Information" }) }))] }) }) }, f.title)) }), !isActive && (_jsx(Card, { title: "Pricing", children: _jsx("div", { className: "p-2 d-flex flex-wrap align-items-center justify-content-between gap-3", children: _jsxs("div", { children: [_jsxs("h2", { className: "mb-0", children: ["$", CRM_MONTHLY_FEE, _jsx("span", { className: "text-muted fs-5", children: "/month" })] }), _jsxs("ul", { className: "mb-0 mt-2 text-muted small", children: [_jsx("li", { children: "Billed monthly, separately from your Rate Remover plan" }), _jsx("li", { children: "First $99 charged today on Get Started (no proration)" }), _jsx("li", { children: "Available with Essentials, Pro, and Elite" })] })] }) }) })), _jsx(Card, { title: "Status", children: _jsx("div", { className: "p-3", children: renderCTA() }) }), _jsx(PaymentModal, { visible: showPayment, onClose: () => setShowPayment(false), onConfirm: handlePaymentConfirm, loading: loading }), _jsx(PaymentWaitingModal, { visible: showWaiting, merchantId: props.merchantId, onClose: () => setShowWaiting(false), onActive: handlePaymentActive }), _jsx(RegistrationModal, { visible: showRegistration, merchant: props.merchant, onClose: () => setShowRegistration(false), onSubmit: handleRegistrationSubmit, loading: loading })] });
};
